import { GridColDef } from "@mui/x-data-grid";
import { Option } from "../../../../../constants/common";
import { applyDefaultColumnSettings } from "../../../../../utils/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PatientEncounterFilterData {
  date_range?: string;
  startDate?: string;
  endDate?: string;
  provider_Name?: string;
  location?: string;
  appointment_Mode?: string;
  appointment_Status?: string;
  search_Patient?: string;
  search_Diagnosis?: string;
}

export const DEFAULT_ENCOUNTER_FILTERS: PatientEncounterFilterData = {
  date_range: undefined,
  startDate: undefined,
  endDate: undefined,
  provider_Name: undefined,
  location: undefined,
  appointment_Mode: undefined,
  appointment_Status: undefined,
  search_Patient: undefined,
  search_Diagnosis: undefined,
};

// ─── Status Color Mapping ────────────────────────────────────────────────────

export const ENCOUNTER_STATUS_MAP: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  COMPLETED: { label: "Completed", color: "#049B22", bgColor: "#E1FCDE" },
  CHECKED_IN: { label: "Checked In", color: "#7B61FF", bgColor: "#EDE8FF" },
  IN_PROGRESS: { label: "In Progress", color: "#943C00", bgColor: "#FFF2D2" },
  NO_SHOW: { label: "No Show", color: "#FF3939", bgColor: "#FFE1E0" },
  CANCELLED: { label: "Canceled", color: "#FF3939", bgColor: "#FFE1E0" },
  SCHEDULED: { label: "Scheduled", color: "#004AB1", bgColor: "#E0EDFF" },
  IN_EXAM: { label: "In Exam", color: "#00B8D9", bgColor: "#E0F7FA" },
  INTAKE_COMPLETED: {
    label: "Intake Completed",
    color: "#36B37E",
    bgColor: "#E3FCEF",
  },
};

// ─── Mode Mapping ────────────────────────────────────────────────────────────

export const ENCOUNTER_MODE_MAP: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  IN_PERSON: { label: "In Person", color: "#004AB1", bgColor: "#E0EDFF" },
  VIRTUAL: { label: "Telehealth", color: "#3538CD", bgColor: "#EEF4FF" },
};

// ─── Filter Options ─────────────────────────────────────────────────────────

export const ENCOUNTER_STATUS_OPTIONS: Option[] = [
  { value: "ALL_STATUSES", label: "All Statuses" },
  { value: "COMPLETED", label: "Completed" },
  { value: "IN_EXAM", label: "In Exam" },
  { value: "CANCELLED", label: "Canceled" },
  // No Show removed from filter — BE does not add No Show encounter records.
  // { value: "NO_SHOW", label: "No Show" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "CHECKED_IN", label: "Checked In" },
];

export const ENCOUNTER_MODE_OPTIONS: Option[] = [
  { value: "ALL_MODE", label: "All Mode" },
  { value: "IN_PERSON", label: "In-Person" },
  { value: "VIRTUAL", label: "Telehealth" },
];

export const ENCOUNTER_TIME_INTERVAL_OPTIONS: Option[] = [
  { value: "TODAY", label: "Today" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "LAST_MONTH", label: "Last Month" },
  { value: "LAST_3_MONTHS", label: "Last 3 Months" },
  { value: "LAST_6_MONTHS", label: "Last 6 Months" },
  { value: "THIS_YEAR", label: "This Year" },
];

// ─── Table Column Definitions ────────────────────────────────────────────────

const SORTABLE_FIELDS = ["dateTime", "status"];

export const getEncounterReportColumns = (
  renderEncounterIdCell: (params: any) => JSX.Element,
  renderStatusCell: (params: any) => JSX.Element,
  renderModeCell: (params: any) => JSX.Element,
  renderPatientName: (params: any) => JSX.Element,
  renderIcdCell: (params: any) => JSX.Element,
  renderCptCell: (params: any) => JSX.Element
): GridColDef[] =>
  applyDefaultColumnSettings(
    [
      {
        field: "encounterUuid",
        headerName: "Visit ID",
        flex: 1,
        minWidth: 130,
        renderCell: renderEncounterIdCell,
      },
      {
        field: "dateTime",
        headerName: "Date & Time",
        flex: 1.2,
        minWidth: 170,
      },
      {
        field: "patientName",
        headerName: "Patient Name",
        flex: 1.2,
        minWidth: 150,
        renderCell: renderPatientName,
      },
      {
        field: "patientId",
        headerName: "Patient ID",
        flex: 0.8,
        minWidth: 110,
      },
      {
        field: "providerName",
        headerName: "Provider",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "visitType",
        headerName: "Type",
        flex: 0.9,
        minWidth: 110,
        renderCell: renderModeCell,
      },
      {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "chiefComplaint",
        headerName: "Reason for Visit",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        renderCell: renderStatusCell,
      },
      {
        field: "icd10Code",
        headerName: "ICD 10 Code",
        flex: 0.8,
        minWidth: 110,
        renderCell: renderIcdCell,
      },
      {
        field: "cptCode",
        headerName: "CPT Code",
        flex: 0.8,
        minWidth: 100,
        renderCell: renderCptCell,
      },
      {
        field: "insurance",
        headerName: "Insurance",
        flex: 1,
        minWidth: 130,
      },
    ],
    SORTABLE_FIELDS
  );

// ─── KPI Card Definitions ────────────────────────────────────────────────────

export interface EncounterKPICardConfig {
  key: string;
  label: string;
  valueKey: string;
  changeKey?: string;
  format: "number" | "percent";
  subtext?: string;
}

export const ENCOUNTER_KPI_CARDS: EncounterKPICardConfig[] = [
  {
    key: "total",
    label: "Total Visits",
    valueKey: "totalEncounters",
    changeKey: "totalEncountersChangePercent",
    format: "number",
    subtext: "vs last month",
  },
  // {
  //   key: "noshow",
  //   label: "No-Show Rate",
  //   valueKey: "noShowRatePercent",
  //   changeKey: "noShowRateChangePercent",
  //   format: "percent",
  //   subtext: "vs last month",
  // },
  {
    key: "unsigned",
    label: "Unsigned Notes",
    valueKey: "unsignedNotesCount",
    format: "number",
  },
];

// ─── Export Columns ─────────────────────────────────────────────────────────
